import {
  adminEventSchema,
  openAccountsEventSchema,
  type AdminEvent,
  type OpenAccountsEvent,
} from '@growup/site-shared';
import { Button, Card, Notice, StatusBadge, Table, type Column } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf } from '../api';
import { formatDateTime } from '../format';
import { SubNav } from '../members/MembersSection';
import { formatEuros } from '../store/labels';
import { BudgetView } from './BudgetView';
import { ChecklistView } from './ChecklistView';
import { EventContentView } from '../social/EventContentView';
import { EventForm } from './EventForm';
import { EVENT_STATUS_LOOK } from './labels';
import { RosterView } from './RosterView';
import { ShiftsView } from './ShiftsView';
import { SpaceView } from './SpaceView';

const pendingOf = (e: AdminEvent) => e.counts.volunteer.pending + e.counts.streamer.pending;

const COLUMNS: Column<AdminEvent>[] = [
  {
    key: 'title',
    header: 'Evento',
    cell: (e) => (
      <span className="flex flex-col">
        <span className="font-semibold">{e.title}</span>
        <span className="text-caption text-gu-ink-muted">
          {formatDateTime(e.startsAt)} · {e.venueName}
        </span>
      </span>
    ),
  },
  {
    key: 'visitors',
    header: 'Visitantes',
    align: 'right',
    cell: (e) =>
      `${String(e.counts.visitor.confirmed)}${e.capacity === null ? '' : `/${String(e.capacity)}`}` +
      (e.counts.visitor.waitlisted > 0 ? ` (+${String(e.counts.visitor.waitlisted)} espera)` : ''),
  },
  {
    key: 'crew',
    header: 'Voluntários · Streamers',
    align: 'right',
    hideOnMobile: true,
    cell: (e) => `${String(e.counts.volunteer.confirmed)} · ${String(e.counts.streamer.confirmed)}`,
  },
  {
    key: 'status',
    header: 'Estado',
    align: 'right',
    cell: (e) => (
      <span className="flex flex-col items-end gap-1">
        <StatusBadge tone={EVENT_STATUS_LOOK[e.status].tone}>
          {EVENT_STATUS_LOOK[e.status].label}
        </StatusBadge>
        {pendingOf(e) > 0 ? (
          <StatusBadge tone="warning">{pendingOf(e)} por aprovar</StatusBadge>
        ) : null}
      </span>
    ),
  },
];

type Tab = 'roster' | 'orcamento' | 'checklist' | 'turnos' | 'espaco' | 'conteudo' | 'dados';

/**
 * Eventos: everything needs `events.manage` (Events Manager, General Admin);
 * the Orçamento tab also needs `events.budget`. `me` is the signed-in account,
 * for the checklist's «Os meus» filter.
 */
export function EventsSection({
  canBudget = false,
  me,
  onOpenSocialPlan,
}: {
  canBudget?: boolean;
  me: string;
  /** Opens a content plan in Redes sociais (only with access to it). */
  onOpenSocialPlan?: ((planId: string) => void) | undefined;
}) {
  const [events, setEvents] = useState<AdminEvent[] | null>(null);
  const [selected, setSelected] = useState<string | 'new' | null>(null);
  const [tab, setTab] = useState<Tab>('roster');
  const [error, setError] = useState<string | null>(null);

  const [openAccounts, setOpenAccounts] = useState<OpenAccountsEvent[]>([]);

  const load = useCallback(() => {
    getJson('/api/site/events', z.array(adminEventSchema))
      .then(setEvents)
      .catch((e: unknown) => setError(messageOf(e)));
    if (canBudget) {
      getJson('/api/site/event-budget/open', z.array(openAccountsEventSchema))
        .then(setOpenAccounts)
        .catch(() => setOpenAccounts([]));
    }
  }, [canBudget]);
  useEffect(load, [load]);

  const event = events?.find((e) => e.id === selected) ?? null;
  const onSaved = (saved: AdminEvent) => {
    setEvents((list) =>
      list === null
        ? [saved]
        : list.some((e) => e.id === saved.id)
          ? list.map((e) => (e.id === saved.id ? saved : e))
          : [saved, ...list],
    );
    setSelected(saved.id);
  };

  if (selected === 'new') {
    return (
      <div className="flex flex-col gap-4">
        <BackButton onClick={() => setSelected(null)} />
        <EventForm
          key="new"
          event={null}
          onSaved={(saved) => {
            onSaved(saved);
            setTab('dados');
          }}
        />
      </div>
    );
  }

  if (event) {
    return (
      <div className="flex flex-col gap-4">
        <BackButton
          onClick={() => {
            setSelected(null);
            load();
          }}
        />
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-display text-title uppercase">{event.title}</h2>
          {event.kind === 'stream_party' ? (
            <StatusBadge tone="info">Stream party</StatusBadge>
          ) : null}
          <StatusBadge tone={EVENT_STATUS_LOOK[event.status].tone}>
            {EVENT_STATUS_LOOK[event.status].label}
          </StatusBadge>
        </div>
        <SubNav
          tabs={[
            { id: 'roster', label: 'Roster' },
            ...(canBudget && event.kind !== 'stream_party'
              ? [{ id: 'orcamento' as const, label: 'Orçamento' }]
              : []),
            { id: 'checklist', label: 'Checklist' },
            { id: 'turnos', label: event.kind === 'stream_party' ? 'Alinhamento' : 'Turnos' },
            ...(event.kind === 'stream_party' ? [] : [{ id: 'espaco' as const, label: 'Espaço' }]),
            { id: 'conteudo', label: 'Conteúdo' },
            { id: 'dados', label: 'Dados do evento' },
          ]}
          current={tab}
          onChange={setTab}
          label="Evento"
        />
        {tab === 'roster' ? <RosterView event={event} onChanged={load} /> : null}
        {tab === 'orcamento' && canBudget ? <BudgetView key={event.id} event={event} /> : null}
        {tab === 'checklist' ? (
          <ChecklistView key={event.id} event={event} events={events ?? []} me={me} />
        ) : null}
        {tab === 'turnos' ? (
          <ShiftsView key={event.id} event={event} events={events ?? []} />
        ) : null}
        {tab === 'espaco' ? <SpaceView key={event.id} event={event} events={events ?? []} /> : null}
        {tab === 'conteudo' ? (
          <EventContentView key={event.id} eventId={event.id} onOpenPlan={onOpenSocialPlan} />
        ) : null}
        {tab === 'dados' ? <EventForm key={event.id} event={event} onSaved={onSaved} /> : null}
      </div>
    );
  }

  return (
    <Card
      headingLevel={2}
      title="Eventos"
      actions={
        <Button size="sm" onClick={() => setSelected('new')}>
          Novo evento
        </Button>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {openAccounts.length > 0 ? (
        <Notice tone="warning" title="Contas por fechar">
          <ul className="flex flex-col gap-1">
            {openAccounts.map((o) => (
              <li key={o.eventId}>
                <button
                  type="button"
                  className="text-left underline"
                  onClick={() => {
                    setSelected(o.eventId);
                    setTab('orcamento');
                  }}
                >
                  {o.title}
                </button>
                {': '}
                {[
                  o.costPlannedCents > 0 ? `${formatEuros(o.costPlannedCents)} por pagar` : null,
                  o.revenuePlannedCents > 0
                    ? `${formatEuros(o.revenuePlannedCents)} por receber`
                    : null,
                  o.missingDocuments > 0
                    ? `${String(o.missingDocuments)} ${o.missingDocuments === 1 ? 'fatura' : 'faturas'} em falta`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
      <Table
        caption="Eventos"
        columns={COLUMNS}
        rows={events ?? []}
        rowKey={(e) => e.id}
        empty={events ? 'Ainda não há eventos.' : 'A carregar…'}
        onRowClick={(e) => {
          setSelected(e.id);
          setTab('roster');
        }}
      />
    </Card>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onClick}>
        ← Eventos
      </Button>
    </div>
  );
}
